import React, { useState, useEffect } from "react";
import { Button, Typography, Form, Checkbox, Row, Col } from "antd";
import moment from "moment";
import isEqual from "lodash.isequal";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import Walkthrough from "./Walkthrough";
import {
  BranchPatientsSelect,
  PatientEffectiveOrderSelect,
  SettingsDataSelect,
} from "../forms/AutoCompleteFormItem";
import CheckBoxItem from "../forms/CheckBoxItem";
import ChildrenAnimationWrapper from "../animations/ChildrenAnimationWrapper";
import AnimationChildWrapper from "../animations/AnimationChildWrapper";
import { InputItem, DatePickerItem, HourPickerItem } from "../forms/FormItems";
import { useIntl } from "react-intl";
import { useParams, useLocation } from "react-router-dom";
import { createSchedulingEvent } from "../../features/scheduling/schedulingSlice";
import { getEmployeeOncallUsage } from "../../api/server";
import { useEmployees_tariffs } from "../../hooks";

const { Title, Text } = Typography;

const EmployeeSchedulingContent = (props) => {
  const {
    activeEmployee,
    closeModal,
    reload,
    currentDate,
    employeePatients,
    initialValues,
  } = props;
  let { branchId } = useParams();
  const location = useLocation();
  const isAdmin = location.pathname.includes("admin");
  const tariffs = useEmployees_tariffs();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [activeEvent, setActiveEvent] = useState({});
  const [useDefault, setUseDefault] = useState(true);
  const [patients, setPatients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [oncallData, setOncallData] = useState(null);
  const saveLoading = useSelector(
    (state) => state.pendingActions["scheduling/create"]
  );
  const [form] = Form.useForm();

  const activePatientFilter = {
    $and: [
      {
        $or: [{ status_id: 1 }],
      },
    ],
  };

  useEffect(() => {
    const fetchData = async () => {
      const params = {
        from: moment(activeEvent.date).startOf("month").format(),
        to: moment(activeEvent.date).endOf("month").format(),
        employeeId: activeEmployee.id,
      };
      const action = await getEmployeeOncallUsage(params);
      setOncallData(action?.data);
    };
    fetchData().catch(console.error);

    return () => {
      setOncallData(null);
    };
  }, []);
  const hasOncallLeft = oncallData?.oncall_total > oncallData?.oncall_used;

  const filterOrders = (items) => {
    const filtered = items.filter(
      (i) =>
        i.effective_from <= moment(activeEvent.start).endOf("day").format() &&
        (i.effective_to === null ||
          i.effective_to >=
            moment(activeEvent.start).startOf("day").format()) &&
        i.order_service_type.supporting_community !== true && //filter out supporting community orders
        i.order_service_type.day_center !== true //filter out day center orders
    );
    if (!isEqual(orders, filtered)) {
      setOrders(filtered);

      if (activeEvent["order_id"]) {
        const selected = filtered.filter(
          (i) => i.id === activeEvent["order_id"]
        )[0];
        if (!selected) {
          form.setFieldsValue({ order_id: null });
          setActiveEvent({ ...activeEvent, order_id: null });
        }
      }
    }
    return filtered;
  };

  useEffect(() => {
    const newEvent = initialValues
      ? {
          ...initialValues,
          tariff_id: activeEmployee.tariff_id,
          use_employee_tariff: true,
        }
      : {
          ...activeEvent,
          tariff_id: activeEmployee.tariff_id,
          start_hour: moment(currentDate)
            .startOf("day")
            .add(360, "minutes")
            .format(),
          date: moment(currentDate).format(),
        };
    form.setFieldsValue(newEvent);
    setActiveEvent(newEvent);
  }, [activeEmployee]);

  const disabledStartHours = () => {
    if (activeEvent["end_hour_next_day"]) {
      return []; // All hours allowed if end is on next day
    }
    var hours = [];
    const hour =
      activeEvent && activeEvent["end_hour"]
        ? moment(activeEvent["end_hour"]).get("hour")
        : 24;
    for (var i = 23; i > hour; i--) {
      hours.push(i);
    }
    return hours;
  };

  const disabledStartMinutes = (selectedHour) => {
    if (activeEvent["end_hour_next_day"]) {
      return []; // All minutes allowed if end is on next day
    }
    const endHour = moment(activeEvent["end_hour"]).get("hour");
    const endMinutes = moment(activeEvent["end_hour"]).get("minute");
    var minutes = [];
    if (selectedHour === endHour) {
      for (var i = 59; i > endMinutes; i--) {
        minutes.push(i);
      }
    }
    return minutes;
  };

  const disabledEndHours = () => {
    if (activeEvent["end_hour_next_day"]) {
      return []; // All hours allowed if end is on next day
    }
    var hours = [];
    for (var i = 0; i < moment(activeEvent["start_hour"]).get("hour"); i++) {
      hours.push(i);
    }
    return hours;
  };

  const disabledEndMinutes = (selectedHour) => {
    if (activeEvent["end_hour_next_day"]) {
      return []; // All minutes allowed if end is on next day
    }
    const startHour = moment(activeEvent["start_hour"]).get("hour");
    const startMinutes = moment(activeEvent["start_hour"]).get("minute");
    var minutes = [];
    if (selectedHour === startHour) {
      for (var i = 0; i < startMinutes; i++) {
        minutes.push(i);
      }
    }
    return minutes;
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    //pop error notification
  };

  const onFinish = async () => {
    const params = {
      patientId: activeEvent.patient_id,
      branchId: activeEvent.patient.branch,
      schedulingEvent: { new: { ...activeEvent } },
    };
    const action = await dispatch(createSchedulingEvent(params));
    if (!action.error) {
      closeModal();
      reload();
    }
  };

  const onValuesChange = (changedValues, allValues) => {
    let startDate = activeEvent.start;
    let endDate = activeEvent.end;
    let patient = activeEvent.patient;
    let duration_minutes = activeEvent.duration_minutes;

    if (allValues["date"] && allValues["start_hour"]) {
      const startMinutes = moment(allValues["start_hour"]).minutes();
      const startHours = moment(allValues["start_hour"]).hours();
      var mmt = moment(allValues["date"]);
      mmt.hours(startHours);
      mmt.minutes(startMinutes);
      var mmtMidnight = mmt.clone().startOf("day");
      var diffMinutes = mmt.diff(mmtMidnight, "minutes");
      startDate = moment(allValues["date"])
        .clone()
        .startOf("day")
        .add(diffMinutes, "minutes")
        .format();

      if (allValues["end_hour"]) {
        const endMinutes = moment(allValues["end_hour"]).minutes();
        const endHours = moment(allValues["end_hour"]).hours();
        var endMmt = moment(allValues["date"]);
        endMmt.hours(endHours);
        endMmt.minutes(endMinutes);

        // Check if end_hour_next_day is true
        if (allValues["end_hour_next_day"]) {
          endMmt.add(1, "day");
        }

        endDate = endMmt.format();

        // If end_hour_next_day is false and end date is before start date
        if (
          !allValues["end_hour_next_day"] &&
          moment(endDate).isBefore(moment(startDate))
        ) {
          endDate = moment(startDate).format();
          // Update the form with the corrected end_hour
          form.setFieldsValue({
            end_hour: moment(startDate),
          });
        }
      }
      duration_minutes = moment(endDate).diff(moment(startDate), "minutes");
    }

    if (allValues["patient_id"]) {
      const selected = patients.filter(
        (i) => i.id === allValues["patient_id"]
      )[0];
      if (selected) {
        patient = selected;
      }
    } else {
      patient = null;
    }
    const selectedOrder = orders.find((i) => i.id === allValues["order_id"]);
    const client_salary_tariff_id = selectedOrder?.client?.salary_tariff_id;

    const updatedEvent = {
      ...activeEvent,
      ...allValues,
      date: startDate,
      start: startDate,
      end: endDate,
      duration_minutes: duration_minutes,
      patient: patient,
      order_id: allValues["order_id"],
      patient_id: allValues["patient_id"],
      client_tariff_id: useDefault ? null : allValues["client_tariff_id"],
      tariff_id: client_salary_tariff_id || allValues["tariff_id"],
      transportation_cost: allValues["transportation_cost"],
      employee_id: activeEmployee.id,
    };

    setActiveEvent(updatedEvent);

    if (allValues["order_id"] && !allValues["patient_id"]) {
      form.setFieldsValue({ order_id: null });
    }
    if (client_salary_tariff_id && !allValues["tariff_id"]) {
      form.setFieldsValue({ tariff_id: client_salary_tariff_id });
    }
  };
  const onPatientsDataChanged = (list) => {
    setPatients(list);
  };

  const onDefaultTariffChange = (e) => {
    setUseDefault(e.target.checked);
  };
  const isDisabled =
    !activeEvent.date ||
    !activeEvent.start ||
    !activeEvent.start ||
    !activeEvent.end ||
    !activeEvent.patient ||
    !activeEvent.order_id ||
    (!useDefault && !activeEvent.client_tariff_id) ||
    !activeEvent.tariff_id;

  const activeTariffFilter = {
    $and: [
      {
        $or: [{ active: true }],
      },
    ],
  };

  const handleEndOpenChange = (open) => {
    if (open && !activeEvent.end_hour && activeEvent.start_hour) {
      form.setFieldsValue({
        ...activeEvent,
        end_hour: moment(activeEvent.start_hour)
          .clone()
          .add(1, "hour")
          .format(),
      });
    }
  };

  const selectedOrder = orders.find((i) => i.id === activeEvent.order_id);
  const client_salary_tariff_id = selectedOrder?.client?.salary_tariff_id;
  const client_salary_tariff = tariffs.find(
    (i) => i.id === client_salary_tariff_id
  );
  const tariff = client_salary_tariff || activeEmployee?.tariff;

  // const client_salary_tariff_id = activeEvent?.order?.client?.salary_tariff_id;
  // const client_salary_tariff = tariffs.find(
  //   (i) => i.id === client_salary_tariff_id
  // );
  // const tariff = client_salary_tariff || activeEvent.employee?.tariff;

  return (
    <div className="details-slide">
      <div className="details-outer">
        <div className="details">
          <div className="main">
            <Title level={2}>
              {intl.formatMessage({
                id: "app.scheduling.newEmployeeScheduling",
              })}
            </Title>
            <Form
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              scrollToFirstError
              onValuesChange={onValuesChange}
              form={form}
              className="formBody"
            >
              <ChildrenAnimationWrapper>
                <Row>
                  <Col key={1} span={12} style={{ padding: "0 20px" }}>
                    <AnimationChildWrapper key={1} itemKey={1}>
                      <DatePickerItem
                        name="date"
                        category="scheduling"
                        required={true}
                      />
                    </AnimationChildWrapper>
                    <AnimationChildWrapper key={3} itemKey={3}>
                      <HourPickerItem
                        name="start_hour"
                        picker="time"
                        format={"HH:mm"}
                        category="scheduling"
                        disabledMinutes={disabledStartMinutes}
                        disabledHours={disabledStartHours}
                        required={true}
                      />
                    </AnimationChildWrapper>

                    <AnimationChildWrapper key={5} itemKey={5}>
                      <BranchPatientsSelect
                        name="patient_id"
                        category="tasks"
                        getPatient={branchId !== null || isAdmin}
                        extraParams={{
                          scopePrefix: isAdmin ? `` : `branches/${branchId}/`,
                          filter: activePatientFilter,
                        }}
                        items={employeePatients}
                        onDataChanged={onPatientsDataChanged}
                        required={true}
                      />
                    </AnimationChildWrapper>

                    <AnimationChildWrapper key={7} itemKey={7}>
                      <SettingsDataSelect
                        name="tariff_id"
                        category="scheduling"
                        settingsdataName={"tariffs"}
                        required={true}
                        type="list"
                        extraParams={{
                          filter: activeTariffFilter,
                        }}
                      />
                    </AnimationChildWrapper>
                    {activeEmployee?.tariff && (
                      <AnimationChildWrapper key={9} itemKey={9}>
                        <CheckBoxItem
                          name="use_employee_tariff"
                          category="employee"
                          style={{ marginBottom: 0 }}
                        />

                        <Text
                          ellipsis={{
                            tooltip: `${tariff?.name} (${tariff?.cost})`,
                          }}
                          style={{
                            display: "block",
                            width: "240px",
                            paddingRight: "24px",
                          }}
                        >
                          {`${tariff?.name} (${tariff?.cost})`}
                        </Text>
                      </AnimationChildWrapper>
                    )}

                    <AnimationChildWrapper key={11} itemKey={11}>
                      <Checkbox
                        onChange={onDefaultTariffChange}
                        checked={useDefault}
                      >
                        {intl.formatMessage({
                          id: "app.scheduling.defaultTariff",
                        })}
                      </Checkbox>
                      <div style={{ height: "10px" }} />
                      {!useDefault && (
                        <SettingsDataSelect
                          name={"client_tariff_id"}
                          category={"scheduling"}
                          settingsdataName={"client_tariffs"}
                          paramName={"cost"}
                          extraParams={{
                            filter: activeTariffFilter,
                          }}
                          type="list"
                          required={true}
                        />
                      )}
                    </AnimationChildWrapper>
                  </Col>
                  <Col key={2} span={12} style={{ padding: "0 20px" }}>
                    <AnimationChildWrapper key={2} itemKey={2}>
                      <div style={{ height: "62.5px", marginBottom: "24px" }} />
                    </AnimationChildWrapper>
                    <AnimationChildWrapper key={4} itemKey={4}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <HourPickerItem
                          name="end_hour"
                          picker="time"
                          format={"HH:mm"}
                          category="scheduling"
                          disabledMinutes={disabledEndMinutes}
                          disabledHours={disabledEndHours}
                          required={true}
                          onOpenChange={handleEndOpenChange}
                        />
                        <CheckBoxItem
                          name="end_hour_next_day"
                          category="scheduling"
                          style={{ marginBottom: 0 }}
                        />
                      </div>
                    </AnimationChildWrapper>
                    <AnimationChildWrapper key={6} itemKey={6}>
                      <PatientEffectiveOrderSelect
                        name={"order_id"}
                        category={"scheduling"}
                        required={true}
                        params={{
                          patientId:
                            activeEvent.patient && activeEvent.patient.id,
                        }}
                        filterItems={filterOrders}
                        extraParams={{
                          scopePrefix: isAdmin ? `` : `branches/${branchId}/`,
                        }}
                        waitForFetch={
                          !activeEvent.patient_id || !activeEvent.start
                        }
                        disabled={!activeEvent.patient_id || !activeEvent.start}
                      />
                    </AnimationChildWrapper>

                    <AnimationChildWrapper key={8} itemKey={8}>
                      <div>
                        <InputItem
                          name="transportation_cost"
                          category="scheduling"
                          number={true}
                          twoDigitsNumbers={true}
                          onlyPositiveNumbers={true}
                          style={{ margin: 0 }}
                        />
                        {activeEmployee && (
                          <Text>
                            {
                              <FormattedMessage
                                id={`app.forms.employee.${activeEmployee.transportation_cost_strategy}`}
                              />
                            }
                          </Text>
                        )}
                      </div>
                    </AnimationChildWrapper>
                    {activeEmployee?.oncall && (
                      <AnimationChildWrapper key={10} itemKey={10}>
                        <div>
                          <CheckBoxItem
                            name={"oncall"}
                            category="scheduling"
                            style={{ margin: 0 }}
                            disabled={!hasOncallLeft}
                          />
                        </div>
                      </AnimationChildWrapper>
                    )}
                  </Col>
                  <Row style={{ width: "100%" }}>
                    <Col span={24}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: "10px",
                        }}
                      >
                        <Button
                          disabled={isDisabled}
                          type="primary"
                          htmlType="submit"
                          loading={saveLoading}
                        >
                          {
                            <FormattedMessage id="app.actions.save_new_scheduling" />
                          }
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Row>
              </ChildrenAnimationWrapper>
            </Form>
          </div>
        </div>
      </div>
      <Walkthrough
        activeEvent={activeEvent}
        orders={orders}
        type={"scheduling"}
        fromEmployee={true}
      />
    </div>
  );
};

export default EmployeeSchedulingContent;
